import { sum } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { TableContentData, TableElementChanges } from '../../../../common/table/TableTypes';
import useRefCallback from '../../../utils/react/useRefCallback';
import { ActiveTableSession, FlushReason } from '../store/hotTableStoreTypes';
import { updateTableElement } from '../tableActions';
import {
    convertHotInstanceToRaw,
    doesHotSnapshotMatchTableState,
} from '../utils/hotInstanceUtils/persistence/hotTablePersistenceUtils';
import { MNHotInstance } from '../tableHotInstanceTypes';
import useThrottledCallback from '../../../utils/react/useThrottledCallback';

// We need to throttle table updates to avoid spamming redux with too many large
// updates, as an ELEMENT_UPDATE for a table sends the entire table data every time.
// Note that this just governs edits within a single table editing session;
// deselecting the table will trigger an immediate flush.
//
// Values are just guesstimates, not scientifically derived.
const getDebounceTimeForHotInstance = (hotInstance: MNHotInstance | null): number => {
    if (!hotInstance) return 0;

    const h = hotInstance.countRows() || 0;
    const w = hotInstance.countCols() || 0;
    const cellCount = h * w;

    // anything up to 10x10 can flush pretty rapidly
    if (cellCount < 100) return 50;

    // larger tables should slow down some
    if (cellCount < 400) return 200;

    // and give an even bigger buffer for very large tables
    return 1000;
};

type UseHotToReduxSessionControlsProps = {
    elementId: string;
    tableInstance: MNHotInstance | null;
    gridSize: number;
    elementWidth: number | undefined;
    tableContentData: TableContentData | undefined;
    tableContentColWidthsGU: number[] | undefined;
};

/**
 * Owns the active-table session controls and the refs they depend on, while keeping a stable session object.
 */
export const useHotToReduxSessionControls = ({
    elementId,
    tableInstance,
    gridSize,
    elementWidth,
    tableContentData,
    tableContentColWidthsGU,
}: UseHotToReduxSessionControlsProps): ActiveTableSession => {
    const elementIdRef = useRef(elementId);
    const tableInstanceRef = useRef(tableInstance);
    const gridSizeRef = useRef(gridSize);
    const elementWidthRef = useRef(elementWidth);
    const tableContentDataRef = useRef(tableContentData);
    const tableContentColWidthsGURef = useRef(tableContentColWidthsGU);
    const isDirtyRef = useRef(false);

    const dispatch = useDispatch();
    const dispatchUpdateTableElement = useCallback((arg) => dispatch(updateTableElement(arg)), [dispatch]);

    // Keep the latest inputs available to stable session helpers without recreating those helpers, via refs.
    useEffect(() => {
        elementIdRef.current = elementId;
        tableInstanceRef.current = tableInstance;
        gridSizeRef.current = gridSize;
        elementWidthRef.current = elementWidth;
        tableContentDataRef.current = tableContentData;
        tableContentColWidthsGURef.current = tableContentColWidthsGU;
    }, [elementId, elementWidth, gridSize, tableContentColWidthsGU, tableContentData, tableInstance]);

    /**
     * Mutation observers mark the session as dirty immediately.
     */
    const markDirtyInternal = useRefCallback(() => {
        isDirtyRef.current = true;
    }, []);

    /**
     * Flush serializes the live HOT snapshot back into persisted element state, but only if Redux is behind.
     */
    const flushNow = useRefCallback(() => {
        if (!isDirtyRef.current) return;
        if (!tableInstanceRef.current) return;

        const tableSnapshot = convertHotInstanceToRaw(tableInstanceRef.current, gridSizeRef.current);

        const isTableSnapshotEqual = doesHotSnapshotMatchTableState({
            tableSnapshot,
            tableContentData: tableContentDataRef.current,
            tableContentColWidthsGU: tableContentColWidthsGURef.current,
        });

        if (isTableSnapshotEqual) {
            isDirtyRef.current = false;
            return;
        }

        const width = sum(tableSnapshot.colWidthsGU);
        const changes: TableElementChanges = { tableContent: tableSnapshot };

        if (width !== elementWidthRef.current) {
            changes.width = width;
        }

        dispatchUpdateTableElement({
            id: elementIdRef.current,
            changes,
        });

        isDirtyRef.current = false;
    }, [dispatchUpdateTableElement]);

    const throttleDelay = getDebounceTimeForHotInstance(tableInstance);
    const throttledFlush = useThrottledCallback(flushNow, throttleDelay, [flushNow, throttleDelay]);

    /**
     * Marks the session dirty and attempts a flush.
     */
    const markDirty = useRefCallback(
        (_reason?: FlushReason) => {
            markDirtyInternal();
            throttledFlush();
        },
        [markDirtyInternal, throttledFlush],
    );

    /**
     * Marks the session dirty and flushes immediately.
     */
    const markDirtyAndFlush = useRefCallback(
        (_reason?: FlushReason) => {
            markDirtyInternal();
            throttledFlush.cancel();
            flushNow();
        },
        [throttledFlush, flushNow, markDirtyInternal],
    );

    useEffect(() => {
        isDirtyRef.current = false;

        return () => {
            throttledFlush.cancel();
            isDirtyRef.current = false;
        };
    }, [throttledFlush, tableInstance]);

    // Expose a stable session object through the active-table store so toolbar/popup consumers can rely on it.
    const sessionRef = useRef<ActiveTableSession>({
        markDirty: (reason?: FlushReason) => markDirty(reason),
        markDirtyAndFlush: (reason?: FlushReason) => markDirtyAndFlush(reason),
        isDirty: () => isDirtyRef.current,
    });

    return sessionRef.current;
};
